import { useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from 'api'
import { useAuth } from 'context/AuthContext'
import { parseError } from 'utils/error-handler'
import { GetUpdatedPaymentOperationsOverviewDto } from '../../../../generated-api'
import { useState } from 'react'

export const usePaymentOperationsOverview = () => {
    const { getAccessToken } = useAuth()

    const query = useQuery(
        ['payment-operations'],
        async () => {
            return apiClient.PaymentOperationsOverviewApi.getPaymentOperationsOverview({
                headers: { Authorization: `Bearer ${await getAccessToken()}` },
            }).catch(parseError)
        },

        {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            enabled: true,
            staleTime: 1000 * 60 * 5,
        }
    )

    return query
}

export const useUpdatedPaymentOperations = (originalIds: GetUpdatedPaymentOperationsOverviewDto | undefined) => {
    const { getAccessToken } = useAuth()

    return useQuery(
        ['payment-operations-updated', originalIds],
        async () => {
            const token = await getAccessToken()

            return apiClient.PaymentOperationsOverviewApi.getUpdatedOverview(
                { getUpdatedPaymentOperationsOverviewDto: originalIds! },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            )
        },
        {
            enabled: !!originalIds,
            staleTime: 5 * 60 * 1000,
        }
    )
}

export const useResourceFriendlyPaymentOperations = () => {
    const { getAccessToken } = useAuth()
    const queryClient = useQueryClient()

    const [originalIds, setOriginalIds] = useState<GetUpdatedPaymentOperationsOverviewDto | null>(null)

    // Fetch full data only once
    const fetchInitial = useQuery(
        ['payment-operations-initial'],
        async () => {
            const token = await getAccessToken()
            const data = await apiClient.PaymentOperationsOverviewApi.getPaymentOperationsOverview({
                headers: { Authorization: `Bearer ${token}` },
            }).catch(parseError)

            const ids = {
                pspNotificationIds: data.failedPspNotifications.map((x) => x.pspNotificationId),
                paymentIds: data.nonCapturedPayments.map((x) => x.paymentId),
                unknownStatePaymentIds: data.unknownStatePayments.map((x) => x.paymentId),
            }

            setOriginalIds(ids)
            queryClient.setQueryData(['payment-operations-updated', ids], data)

            return data
        },
        {
            staleTime: 5 * 60 * 1000,
        }
    )

    // Fetch updated data by IDs (but only once we have them)
    const fetchUpdated = useQuery(
        ['payment-operations-updated', originalIds],
        async () => {
            const token = await getAccessToken()
            return apiClient.PaymentOperationsOverviewApi.getUpdatedOverview(
                { getUpdatedPaymentOperationsOverviewDto: originalIds! },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            ).catch(parseError)
        },
        {
            enabled: !!originalIds,
            staleTime: 5 * 60 * 1000,
        }
    )

    const data = fetchUpdated.data ?? fetchInitial.data

    return {
        data,
        isLoading: fetchInitial.isLoading || fetchUpdated.isLoading,
        refetch: fetchUpdated.refetch, // for refresh button
    }
}
